import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Menu,
  MenuItem,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Chat as ChatIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

interface ChatHistoryItem {
  id: string;
  title: string;
  timestamp: Date;
  type: 'email' | 'general' | 'template';
  isActive?: boolean;
}

interface ChatHistoryProps {
  conversations: ChatHistoryItem[];
  activeConversationId?: string;
  onConversationSelect: (id: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  onClearHistory: () => void;
}

const HistoryContainer = styled(Box)<{ expanded: boolean }>(({ theme, expanded }) => ({
  width: expanded ? '280px' : '68px',
  minWidth: expanded ? '280px' : '68px',
  maxWidth: expanded ? '280px' : '68px',
  height: '100%',
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRight: '1px solid #e5e5e5',
  transition: 'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease',
  flexShrink: 0,
  position: 'relative',
  zIndex: 10,
}));

const HistoryHeader = styled(Box)<{ expanded: boolean }>(({ theme, expanded }) => ({
  padding: expanded ? theme.spacing(2) : theme.spacing(1.5),
  borderBottom: '1px solid #e5e5e5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: expanded ? 'space-between' : 'center',
  minHeight: '48px',
}));

const NewChatButton = styled(Button)<{ expanded: boolean }>(({ theme, expanded }) => ({
  margin: expanded ? theme.spacing(2) : theme.spacing(1),
  width: expanded ? 'calc(100% - 32px)' : '36px',
  minWidth: expanded ? 'auto' : '36px',
  maxWidth: expanded ? 'calc(100% - 32px)' : '36px',
  height: expanded ? 'auto' : '36px',
  justifyContent: expanded ? 'flex-start' : 'center',
  padding: expanded ? theme.spacing(1.5, 2) : theme.spacing(1),
  borderRadius: '6px',
  backgroundColor: 'white',
  border: '1px solid #d1d5db',
  color: '#374151',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  boxSizing: 'border-box',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: '#f9fafb',
    borderColor: '#9ca3af',
  },
  '& .MuiButton-startIcon': {
    marginRight: expanded ? theme.spacing(1) : 0,
    flexShrink: 0,
  },
}));

const ConversationList = styled(List)<{ expanded: boolean }>(({ theme, expanded }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: expanded ? theme.spacing(1) : theme.spacing(0.5),
  width: '100%',
  boxSizing: 'border-box',
  '& .MuiListItem-root': {
    borderRadius: '6px',
    marginBottom: theme.spacing(0.5),
    padding: 0,
    width: '100%',
  },
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#d1d5db',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#9ca3af',
  },
}));

const ConversationItem = styled(ListItemButton)<{ isActive?: boolean; expanded: boolean }>(({ theme, isActive, expanded }) => ({
  padding: expanded ? theme.spacing(1.5, 2) : theme.spacing(1),
  borderRadius: '6px',
  backgroundColor: isActive ? '#f3f4f6' : 'transparent',
  color: isActive ? '#111827' : '#374151',
  minHeight: expanded ? '44px' : '36px',
  justifyContent: expanded ? 'flex-start' : 'center',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  border: isActive ? `2px solid ${theme.palette.secondary.main}` : '2px solid transparent',
  '&:hover': {
    backgroundColor: isActive ? '#f3f4f6' : '#f9fafb',
    borderColor: isActive ? theme.palette.secondary.main : '#d1d5db',
  },
  '& .MuiListItemIcon-root': {
    color: isActive ? theme.palette.secondary.main : '#6b7280',
    minWidth: expanded ? '32px' : '24px',
    marginRight: expanded ? theme.spacing(1) : 0,
    flexShrink: 0,
  },
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: isActive ? 600 : 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: expanded ? '180px' : '0px',
    color: isActive ? theme.palette.secondary.dark : '#374151',
  },
  '& .MuiListItemText-secondary': {
    fontSize: '12px',
    color: isActive ? '#6b7280' : '#9ca3af',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: expanded ? '180px' : '0px',
  },
}));

const EmptyState = styled(Box)<{ expanded: boolean }>(({ theme, expanded }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: expanded ? theme.spacing(4, 2) : theme.spacing(2, 1),
  color: '#6b7280',
  textAlign: 'center',
}));

const ChatHistory: React.FC<ChatHistoryProps> = ({
  conversations,
  activeConversationId,
  onConversationSelect,
  onNewConversation,
  onDeleteConversation,
  onClearHistory,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string>('');

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, conversationId: string) => {
    event.stopPropagation();
    setSelectedConversationId(conversationId);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConversationId('');
  };

  const handleDelete = () => {
    if (selectedConversationId) {
      onDeleteConversation(selectedConversationId);
      handleMenuClose();
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <ChatIcon fontSize="small" />;
      case 'template':
        return <HistoryIcon fontSize="small" />;
      default:
        return <ChatIcon fontSize="small" />;
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - timestamp.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return timestamp.toLocaleDateString();
    }
  };

  return (
    <HistoryContainer expanded={expanded}>
      {/* Header */}
      <HistoryHeader expanded={expanded}>
        {expanded ? (
          <>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: '#111827',
              fontSize: '16px'
            }}>
              Chat History
              {conversations.length > 0 && (
                <Typography 
                  component="span" 
                  sx={{ 
                    fontSize: '12px', 
                    color: '#6b7280', 
                    fontWeight: 400,
                    ml: 1
                  }}
                >
                  ({conversations.length})
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Collapse Sidebar">
                <IconButton 
                  size="small" 
                  onClick={toggleExpanded}
                  sx={{ 
                    color: '#6b7280',
                    '&:hover': { 
                      backgroundColor: '#f3f4f6',
                      color: '#374151'
                    }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        ) : (
          <Tooltip title="Expand Sidebar">
            <IconButton 
              size="small" 
              onClick={toggleExpanded}
              sx={{ 
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: '#f3f4f6',
                  color: '#374151'
                }
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </HistoryHeader>

      {/* New Chat Button */}
      <NewChatButton
        expanded={expanded}
        startIcon={expanded ? <AddIcon /> : undefined}
        onClick={onNewConversation}
      >
        {expanded ? 'New Chat' : <AddIcon />}
      </NewChatButton>

      {expanded && <Divider sx={{ borderColor: '#e5e5e5', margin: '0 16px' }} />}

      {/* Conversations List */}
      <ConversationList expanded={expanded}>
        {conversations.length === 0 ? (
          <EmptyState expanded={expanded}>
            {expanded ? (
              <>
                <ChatIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>
                  No conversations yet
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '12px', textAlign: 'center' }}>
                  Click on a prompt to start your first chat
                </Typography>
              </>
            ) : (
              <ChatIcon sx={{ fontSize: 24, opacity: 0.3 }} />
            )}
          </EmptyState>
        ) : (
          conversations.map((conversation) => (
            <ListItem
              key={conversation.id}
              disablePadding
              secondaryAction={
                expanded && (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(event) => handleMenuOpen(event, conversation.id)}
                    sx={{ 
                      color: '#6b7280',
                      '&:hover': { 
                        backgroundColor: '#f3f4f6',
                        color: '#374151'
                      }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )
              }
            >
              <ConversationItem
                expanded={expanded}
                isActive={conversation.id === activeConversationId}
                onClick={() => onConversationSelect(conversation.id)}
              >
                <ListItemIcon>
                  {getTypeIcon(conversation.type)}
                </ListItemIcon>
                {expanded && (
                  <ListItemText
                    primary={
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '180px'
                        }}
                      >
                        {conversation.title}
                      </Typography>
                    }
                    secondary={formatTimestamp(conversation.timestamp)}
                  />
                )}
              </ConversationItem>
            </ListItem>
          ))
        )}
      </ConversationList>

      {/* Delete Confirmation Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem 
          onClick={handleDelete}
          sx={{ 
            color: '#dc2626',
            '&:hover': { backgroundColor: '#fef2f2' }
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} />
          </ListItemIcon>
          <ListItemText primary="Delete Conversation" />
        </MenuItem>
      </Menu>
    </HistoryContainer>
  );
};

export default ChatHistory;
